// ============================================================
// DEFENCE CHAIN - Pure functions for multi-step defence resolution.
//
// Models the Deathwatch RPG defence sequence: Dodge → Step Aside
// → Parry → Wall of Steel, with each successful reaction removing
// 1 + DoS hits from the incoming attack.
//
// Zero React dependency.
// ============================================================
import type { Target } from '../types';
import { rollD100, calcDoS, calcDoF } from './dice';
import { applyUnnaturalDos, DEFAULT_UNNATURAL_DOS_MODE } from './unnaturalDos';
import type { UnnaturalDosMode } from './unnaturalDos';
import { hasWS, getWSP, parseWS } from './weaponSpecials';
import { resolveReactionAvailability } from './reactionAvailability';
import { condType } from './conditions';
import type { ConditionModOptions } from './conditionModifiers';

// ---- Types ----

type DefenceStepType = 'dodge' | 'step_aside_dodge' | 'parry' | 'wall_of_steel_parry';

export interface DefenceStep {
  type: DefenceStepType;
  label: string;
  skill: number;
  /** Governing characteristic's raw value (agility / weaponSkill, no stance) - unnatural DoS house rule. */
  charValue?: number;
  /** Unnatural ×N of the governing characteristic (Target.unnaturalAgility/WeaponSkill). */
  multiplier?: number;
  /** A table ruling typed on the banner (v8.366.0), already inside `skill`; kept so the ledger can say so. */
  manualMod?: number;
}

export interface DefenceStepResult {
  step: DefenceStep;
  roll: number;
  success: boolean;
  dos: number;
  dof: number;
  avoided: number;
  counterAttackTriggered: boolean;
  /** Unnatural DoS house-rule note when it fired, e.g. "Unnatural ×2: +4 DoS (bonus rule)". */
  unnaturalNote?: string | null;
}

// ---- Attacker-side denial (v8.287.0) ----

/**
 * What the ATTACKER's weapon does to the defender's reactions - the
 * generalisation of the old Flexible-only check. One derivation for
 * the chain, the defence buttons and the defence forecast.
 */
export interface AttackerDefenceMods {
  /** Unerring: the shot cannot be dodged. */
  blocksDodge: boolean;
  /** Flexible, or Ethereal met by a parry weapon with no field or force matrix. */
  blocksParry: boolean;
  /** Feinting(-X) / Telegraphed(+X), applied to the dodge attempt. */
  dodgeMod: number;
  /** Same pair, applied to the parry attempt. */
  parryMod: number;
}

export function attackerDefenceMods(
  attackerSpecials?: string[],
  parryWeaponSpecials?: string[],
  /** The attacker's reaction exposures (v8.368.3), carried on the target as `attackerReactionMods`. */
  exposure?: { dodge: number; parry: number } | null,
): AttackerDefenceMods {
  const sp = attackerSpecials || [];
  const mod = getWSP(sp, 'Telegraphed') - getWSP(sp, 'Feinting');
  // Ethereal: only a PowerField or Force weapon can catch the blade.
  // A statblock's parry weapon is unknown - treated as mundane, which
  // is the honest reading of an empty list.
  const etherealBlocked =
    hasWS(sp, 'Ethereal') &&
    !(parryWeaponSpecials || []).some((s) => {
      const id = parseWS(s).id;
      return id === 'PowerField' || id === 'Force';
    });
  return {
    blocksDodge: hasWS(sp, 'Unerring'),
    blocksParry: hasWS(sp, 'Flexible') || etherealBlocked,
    dodgeMod: mod + (exposure?.dodge ?? 0),
    parryMod: mod + (exposure?.parry ?? 0),
  };
}

// ---- Step availability ----

export const isDodgeType = (t: string | undefined) => t === 'dodge' || t === 'step_aside_dodge';
const isParryType = (t: DefenceStepType) => t === 'parry' || t === 'wall_of_steel_parry';

/**
 * One kind of reaction - dodge or parry - as the chain sees it: whether
 * it is on offer at all, what it rolls against, and how much of it the
 * completed steps have used. The two kinds are symmetric apart from
 * these values, which is why one builder serves both.
 */
interface ReactionKind {
  primary: DefenceStepType;
  extra: DefenceStepType;
  label: string;
  /** The follow-up's label: the talent's while it pays, the stance's after. */
  extraLabels: [talent: string, stance: string];
  available: boolean;
  charValue: number;
  multiplier: number;
  /** Condition and attacker modifiers, already summed in that order. */
  mods: [condition: number, attacker: number];
  /** Extra reactions of this kind the TALENT pays for (Step Aside / Wall of Steel). */
  additional: number;
  /** Completed steps of this kind, primary included. */
  used: number;
  primaryTaken: boolean;
}

/**
 * Defensive Stance's extra Reaction (v8.298.1). The defence ledger had
 * advertised "STANCE +1 REACTION" since v8.216.0 while the chain never
 * offered it - the label was the whole implementation.
 *
 * RAW grants ONE Reaction, spendable on a dodge OR a parry, so it cannot
 * be modelled as +1 to each talent count. It is a single budget: extras
 * beyond what the TALENTS pay for are the stance's, and once one has been
 * taken the stance is spent whichever kind it was.
 *
 * Deliberately NOT gated on `applyConditionMods`: the ledger entry is not
 * either, and a stance the UI promises but the chain refuses is the bug
 * it exists to fix. The -20 the stance puts on the ATTACKER is a
 * condition modifier and stays gated, as it always was.
 */
function stanceExtraOf(target: Target, dodge: ReactionKind, parry: ReactionKind): number {
  const inStance = (target.conditions || []).some((c) => condType(c) === 'Ds');
  const extraDodgesTaken = Math.max(0, dodge.used - 1);
  const extraParriesTaken = Math.max(0, parry.used - 1);
  const talentExtrasTaken =
    Math.min(extraDodgesTaken, dodge.additional) + Math.min(extraParriesTaken, parry.additional);
  const stanceSpent = extraDodgesTaken + extraParriesTaken > talentExtrasTaken;
  return inStance && !stanceSpent ? 1 : 0;
}

/**
 * The step this kind offers next: the primary until it is taken, then
 * one follow-up per extra Reaction left (the talent's, then the
 * stance's single spare). The talent pays first, so the label names
 * whichever is actually being spent - a defender needs to know the
 * stance is now gone.
 */
function nextStepOf(kind: ReactionKind, stanceExtra: number, manualMod: number): DefenceStep[] {
  if (!kind.available) return [];
  // Defensive Stance grants no dodge/parry bonus since v8.216.0: RAW is
  // -20 on the ATTACKER (the Ds condition's posture rule) and an extra
  // Reaction, both handled elsewhere. The +10 was a house invention.
  const step = (type: DefenceStepType, label: string): DefenceStep => ({
    type,
    label,
    skill: kind.charValue + kind.mods[0] + kind.mods[1] + manualMod,
    charValue: kind.charValue,
    multiplier: kind.multiplier,
    ...(manualMod ? { manualMod } : {}),
  });
  if (!kind.primaryTaken) return [step(kind.primary, kind.label)];
  const extrasUsed = kind.used - 1; // -1 for the primary
  if (extrasUsed >= kind.additional + stanceExtra) return [];
  return [step(kind.extra, kind.extraLabels[extrasUsed < kind.additional ? 0 : 1])];
}

type Gate = ReturnType<typeof resolveReactionAvailability>;
type Usage = Pick<ReactionKind, 'used' | 'primaryTaken'>;

function dodgeKind(target: Target, gate: Gate, atk: AttackerDefenceMods, usage: Usage): ReactionKind {
  return {
    primary: 'dodge',
    extra: 'step_aside_dodge',
    label: 'Dodge',
    extraLabels: ['Dodge Again (Step Aside)', 'Dodge Again (Defensive Stance)'],
    available: gate.canDodge && !atk.blocksDodge,
    charValue: target.dodge.agility,
    multiplier: target.unnaturalAgility || 1,
    mods: [gate.dodgeMod, atk.dodgeMod],
    additional: target.dodge.additionalDodges || 0,
    ...usage,
  };
}

/** A parry is on offer against melee, or against a ranged attack with Deflect Shot. */
function parryKind(target: Target, isMelee: boolean, gate: Gate, atk: AttackerDefenceMods, usage: Usage): ReactionKind {
  return {
    primary: 'parry',
    extra: 'wall_of_steel_parry',
    label: 'Parry',
    extraLabels: ['Parry Again (Wall of Steel)', 'Parry Again (Defensive Stance)'],
    available: (isMelee || !!target.parry.canParryRanged) && gate.canParry && !atk.blocksParry,
    charValue: target.parry.weaponSkill,
    multiplier: target.unnaturalWeaponSkill || 1,
    mods: [gate.parryMod, atk.parryMod],
    additional: target.parry.additionalParries || 0,
    ...usage,
  };
}

/** What the chain needs to know about each kind, from the target and the attack. */
function reactionKinds(
  target: Target,
  isMelee: boolean,
  completedSteps: DefenceStepResult[],
  attackerSpecials: string[] | undefined,
  availability: { applyConditionMods: boolean; condOptions: ConditionModOptions },
): { dodge: ReactionKind; parry: ReactionKind } {
  // Frenzy, Battle Rage and the condition gates, in one call - the same
  // one the attack prompt and the defence buttons make, so the three
  // cannot disagree about whether a reaction exists.
  const gate = resolveReactionAvailability(target, availability);
  // The attacker's own denial and modifiers (v8.287.0): Unerring blocks
  // the dodge, Flexible/Ethereal block the parry, Feinting/Telegraphed
  // shift both attempts. One derivation, shared with the prompt buttons
  // and the defence forecast.
  const atk = attackerDefenceMods(attackerSpecials, target.parry?.parryWeaponSpecials, target.attackerReactionMods);
  const usage = (isKind: (t: DefenceStepType) => boolean, primary: DefenceStepType): Usage => ({
    used: completedSteps.filter((s) => isKind(s.step.type)).length,
    primaryTaken: completedSteps.some((s) => s.step.type === primary),
  });
  return {
    dodge: dodgeKind(target, gate, atk, usage(isDodgeType, 'dodge')),
    parry: parryKind(target, isMelee, gate, atk, usage(isParryType, 'parry')),
  };
}

/**
 * RAW mode: restrict to one reaction type per attack. In strict
 * Deathwatch rules, a defender picks one reaction type (dodge or parry)
 * per attack. Step Aside adds extra dodges, Wall of Steel adds extra
 * parries, but you cannot mix dodge and parry in the same defence.
 */
function oneKindOnly(target: Target, steps: DefenceStep[], completedSteps: DefenceStepResult[]): DefenceStep[] {
  if (target.allowFullDefenceChain || completedSteps.length === 0) return steps;
  if (completedSteps.some((s) => isDodgeType(s.step.type))) return steps.filter((s) => isDodgeType(s.type));
  if (completedSteps.some((s) => isParryType(s.step.type))) return steps.filter((s) => isParryType(s.type));
  return steps;
}

/**
 * Determine which defence steps are available given the target's capabilities,
 * whether the attack is melee, which steps have already been completed, and
 * how many hits remain.
 */
export function getAvailableDefenceSteps(
  target: Target,
  isMelee: boolean,
  completedSteps: DefenceStepResult[],
  remainingHitCount: number,
  attackerSpecials?: string[],
  applyConditionMods = false,
  condOptions: ConditionModOptions = {},
  /** The banner's table ruling (v8.366.0): every step of this chain carries it. */
  manualMod = 0,
): DefenceStep[] {
  if (remainingHitCount <= 0) return [];
  const availability = { applyConditionMods, condOptions };
  const { dodge, parry } = reactionKinds(target, isMelee, completedSteps, attackerSpecials, availability);
  const stanceExtra = stanceExtraOf(target, dodge, parry);
  const steps = [...nextStepOf(dodge, stanceExtra, manualMod), ...nextStepOf(parry, stanceExtra, manualMod)];
  return oneKindOnly(target, steps, completedSteps);
}

// ---- Step resolution ----

/**
 * Resolve a single defence step: roll vs skill, compute DoS/DoF,
 * and determine how many hits are avoided.
 *
 * Counter-Attack is triggered when a parry succeeds and the target
 * has the counterAttackAvailable flag set.
 */
export function resolveDefenceStep(
  step: DefenceStep,
  remainingHitCount: number,
  counterAttackAvailable: boolean,
  unnaturalDosMode: UnnaturalDosMode = DEFAULT_UNNATURAL_DOS_MODE,
): DefenceStepResult {
  const roll = rollD100();
  const success = roll <= step.skill;
  const baseDos = success ? calcDoS(step.skill, roll) : 0;
  // Unnatural DoS house rule: inflated reaction DoS negates extra hits
  const unnatural = success
    ? applyUnnaturalDos(baseDos, roll, step.skill, step.charValue ?? step.skill, step.multiplier ?? 1, unnaturalDosMode)
    : { dos: 0, bonus: 0, note: null };
  const dos = unnatural.dos;
  const dof = success ? 0 : calcDoF(roll, step.skill);
  const avoided = success ? Math.min(1 + dos, remainingHitCount) : 0;

  const counterAttackTriggered = success && isParryType(step.type) && counterAttackAvailable;

  return { step, roll, success, dos, dof, avoided, counterAttackTriggered, unnaturalNote: unnatural.note };
}

// ---- Aggregation helpers ----

/**
 * Compute the total number of hits avoided across all completed chain steps.
 */
export function totalAvoided(steps: DefenceStepResult[]): number {
  return steps.reduce((sum, s) => sum + s.avoided, 0);
}

/**
 * Check whether any step in the chain triggered Counter-Attack.
 */
export function hasCounterAttack(steps: DefenceStepResult[]): boolean {
  return steps.some((s) => s.counterAttackTriggered);
}

/**
 * Collapse chain results into legacy dodgeResult/parryResult format
 * for backward-compatible narrative formatting.
 *
 * Takes the "best" dodge and parry results from the chain.
 */
export function toLegacyDefenceResults(steps: DefenceStepResult[]): {
  dodgeResult: { roll: number; skill: number; success: boolean; dos: number; avoided: number } | null;
  parryResult: { roll: number; skill: number; success: boolean; dos: number; avoided: number } | null;
} {
  // Aggregate: total avoided across all steps of a kind, last roll for display.
  return {
    dodgeResult: legacyResult(steps.filter((s) => isDodgeType(s.step.type))),
    parryResult: legacyResult(steps.filter((s) => isParryType(s.step.type))),
  };
}

function legacyResult(kindSteps: DefenceStepResult[]) {
  if (kindSteps.length === 0) return null;
  return {
    roll: kindSteps[kindSteps.length - 1].roll,
    skill: kindSteps[0].step.skill,
    success: kindSteps.some((s) => s.success),
    dos: kindSteps.reduce((sum, s) => sum + s.dos, 0),
    avoided: kindSteps.reduce((sum, s) => sum + s.avoided, 0),
  };
}
